import { useState, useEffect, useCallback } from "react";
import { Download, X, Copy, Check, Maximize2, Calendar, Ruler, ChevronLeft, ChevronRight, UserRound, Shirt, Link2, ImagePlus, ArrowUpCircle, Clock, Cpu, Dices } from "lucide-react";
import { ProxyImage } from "./ProxyImage";

export interface GalleryImage {
  id: number;
  prompt: string;
  imageUrl: string;
  imageSize: string | null;
  createdAt: string;
  modelName?: string;
  durationMs?: number;
  seed?: number | null;
}

interface LinkedPerson {
  personId: number;
  name: string;
  primaryImageUrl: string | null;
}

interface LinkedGarment {
  garmentId: number;
  name: string;
  category: string;
  primaryImageUrl: string | null;
}

interface ImageLinks {
  persons: LinkedPerson[];
  garments: LinkedGarment[];
}

interface ImageGalleryProps {
  images: GalleryImage[];
  onOpenLibraryPerson?: (id: number) => void;
  onOpenLibraryGarment?: (id: number) => void;
  onUseAsReference?: (imageUrl: string) => void;
  onUpscale?: (imageUrl: string) => void;
}

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr.includes("T") ? dateStr : dateStr.replace(" ", "T") + "Z");
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return "Upravo sada";
    if (diffMins < 60) return `prije ${diffMins}m`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24) return `prije ${diffHrs}h`;
    const diffDays = Math.floor(diffHrs / 24);
    if (diffDays < 7) return `prije ${diffDays}d`;
    return d.toLocaleDateString("bs-BA", { month: "short", day: "numeric" });
  } catch {
    return dateStr;
  }
}

function extFromBlob(blob: Blob): string {
  const mime = blob.type;
  if (mime.includes("jpeg") || mime.includes("jpg")) return "jpg";
  if (mime.includes("webp")) return "webp";
  if (mime.includes("gif")) return "gif";
  return "png";
}

export function ImageGallery({ images, onOpenLibraryPerson, onOpenLibraryGarment, onUseAsReference, onUpscale }: ImageGalleryProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);
  const [seedCopied, setSeedCopied] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageLinks, setImageLinks] = useState<ImageLinks | null>(null);

  const isOpen = selectedIndex !== null;
  const selectedImage = isOpen ? images[selectedIndex] : null;
  const hasPrev = isOpen && selectedIndex > 0;
  const hasNext = isOpen && selectedIndex < images.length - 1;

  const goTo = useCallback((index: number) => {
    setSelectedIndex(index);
    setCopied(false);
    setSeedCopied(false);
    setImageLoading(true);
    setImageLinks(null);
    // Fetch linked entities
    const img = images[index];
    if (img?.id && typeof img.id === "number") {
      fetch(`/api/images/${img.id}/links`)
        .then((r) => r.ok ? r.json() : null)
        .then((data) => { if (data) setImageLinks(data); })
        .catch(() => {});
    }
  }, [images]);

  const goPrev = useCallback(() => {
    if (selectedIndex !== null && selectedIndex > 0) goTo(selectedIndex - 1);
  }, [selectedIndex, goTo]);

  const goNext = useCallback(() => {
    if (selectedIndex !== null && selectedIndex < images.length - 1) goTo(selectedIndex + 1);
  }, [selectedIndex, images.length, goTo]);

  const close = useCallback(() => {
    setSelectedIndex(null);
    setCopied(false);
    setSeedCopied(false);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, close, goPrev, goNext]);

  const handleCopyPrompt = () => {
    if (!selectedImage) return;
    navigator.clipboard.writeText(selectedImage.prompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = async () => {
    if (!selectedImage) return;
    try {
      const res = await fetch(selectedImage.imageUrl);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lunatik-${selectedImage.id}.${extFromBlob(blob)}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // Fallback: open in new tab
      window.open(selectedImage.imageUrl, "_blank");
    }
  };

  if (images.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground/40">
        <p className="text-sm">Još nema slika</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {images.map((img, i) => (
          <div
            key={img.id}
            className="group relative w-[100px] h-[100px] rounded-xl overflow-hidden border border-border cursor-pointer transition-all hover:border-primary/40 shrink-0 hover-glow"
            onClick={() => goTo(i)}
          >
            <ProxyImage
              src={{ type: "generated", id: img.id }}
              alt={img.prompt}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/20 transition-colors pointer-events-none" />
            <div className="absolute bottom-1 right-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  fetch(img.imageUrl).then((r) => r.blob()).then((blob) => {
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `lunatik-${img.id}.${extFromBlob(blob)}`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }).catch(() => window.open(img.imageUrl, "_blank"));
                }}
                className="w-6 h-6 rounded-md bg-black/60 text-white flex items-center justify-center hover:bg-primary/80"
                title="Preuzmi"
              >
                <Download className="w-3 h-3" />
              </button>
              {onUseAsReference && (
                <button
                  onClick={(e) => { e.stopPropagation(); onUseAsReference(img.imageUrl); }}
                  className="w-6 h-6 rounded-md bg-black/60 text-white flex items-center justify-center hover:bg-primary/80"
                  title="Koristi kao referencu"
                >
                  <ImagePlus className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox carousel */}
      {isOpen && selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-md"
          style={{ backgroundColor: "var(--effect-overlay-bg)" }}
          onClick={close}
        >
          {/* Top bar — counter + close */}
          <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-5 py-4" onClick={(e) => e.stopPropagation()}>
            <span className="text-[12px] font-medium text-white/60 tabular-nums bg-black/30 px-2.5 py-1 rounded-full backdrop-blur-sm">
              {selectedIndex + 1} / {images.length}
            </span>
            <button
              onClick={close}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-black/30 text-white/70 hover:bg-black/50 hover:text-white transition-colors backdrop-blur-sm"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Prev button */}
          <button
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            disabled={!hasPrev}
            className={`absolute left-3 z-20 w-10 h-10 flex items-center justify-center rounded-full backdrop-blur-sm transition-all ${
              hasPrev
                ? "bg-black/30 text-white/80 hover:bg-black/50 hover:text-white cursor-pointer"
                : "bg-black/10 text-white/20 cursor-default"
            }`}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          {/* Next button */}
          <button
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            disabled={!hasNext}
            className={`absolute right-3 z-20 w-10 h-10 flex items-center justify-center rounded-full backdrop-blur-sm transition-all ${
              hasNext
                ? "bg-black/30 text-white/80 hover:bg-black/50 hover:text-white cursor-pointer"
                : "bg-black/10 text-white/20 cursor-default"
            }`}
            style={{ right: "calc(280px + 12px)" }}
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {/* Content — image + side panel */}
          <div
            className="relative flex max-w-[92vw] max-h-[90vh] rounded-2xl overflow-hidden border border-white/10"
            style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.4)" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Image area */}
            <div className="flex-1 flex items-center justify-center bg-black/80 min-w-0 min-h-0 relative" style={{ minWidth: "40vw", minHeight: "50vh" }}>
              {imageLoading && (
                <div className="absolute inset-6 flex items-center justify-center">
                  <div className="w-full h-full rounded-xl bg-white/[0.04] overflow-hidden relative">
                    {/* Shimmer sweep */}
                    <div
                      className="absolute inset-0"
                      style={{
                        background: "linear-gradient(90deg, transparent 0%, white 50%, transparent 100%)",
                        opacity: 0.03,
                        animation: "shimmer-sweep 1.5s ease-in-out infinite",
                      }}
                    />
                    {/* Centered icon placeholder */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-white/[0.05]" />
                      <div className="w-32 h-2 rounded-full bg-white/[0.05]" />
                      <div className="w-20 h-2 rounded-full bg-white/[0.04]" />
                    </div>
                  </div>
                </div>
              )}
              <img
                key={selectedImage.id}
                src={selectedImage.imageUrl}
                alt={selectedImage.prompt}
                className={`max-h-[88vh] max-w-[60vw] object-contain transition-opacity duration-300 ${imageLoading ? "opacity-0" : "opacity-100"}`}
                onLoad={() => setImageLoading(false)}
              />
            </div>

            {/* Info side panel */}
            <div className="w-[280px] shrink-0 flex flex-col bg-card border-l border-border/50 max-h-[90vh]">
              {/* Prompt */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-medium text-muted-foreground/60 uppercase tracking-wider">Prompt</span>
                  <button
                    onClick={handleCopyPrompt}
                    className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-primary transition-colors"
                  >
                    {copied
                      ? <><Check className="w-3 h-3" /> Kopirano</>
                      : <><Copy className="w-3 h-3" /> Kopiraj</>
                    }
                  </button>
                </div>
                <p className="text-[13px] leading-relaxed text-foreground/85">
                  {selectedImage.prompt}
                </p>
              </div>

              {/* Linked entities */}
              {imageLinks && (imageLinks.persons.length > 0 || imageLinks.garments.length > 0) && (
                <div className="border-t border-border/50 px-4 py-3 space-y-2">
                  <div className="flex items-center gap-1">
                    <Link2 className="w-3 h-3 text-muted-foreground/50" />
                    <span className="text-[10px] font-medium text-muted-foreground/60 uppercase tracking-wider">Povezano</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {imageLinks.persons.map((p) => (
                      <button
                        key={`p-${p.personId}`}
                        onClick={() => onOpenLibraryPerson?.(p.personId)}
                        className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/20 hover:bg-emerald-500/20 transition-colors"
                      >
                        {p.primaryImageUrl ? (
                          <ProxyImage src={{ type: "person", id: p.personId }} className="w-4 h-4 rounded-full object-cover" />
                        ) : (
                          <UserRound className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                        )}
                        <span className="text-[10px] font-medium text-emerald-600 dark:text-emerald-400">{p.name}</span>
                      </button>
                    ))}
                    {imageLinks.garments.map((g) => (
                      <button
                        key={`g-${g.garmentId}`}
                        onClick={() => onOpenLibraryGarment?.(g.garmentId)}
                        className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-primary/10 border border-primary/20 hover:bg-primary/20 transition-colors"
                      >
                        {g.primaryImageUrl ? (
                          <ProxyImage src={{ type: "garment", id: g.garmentId }} className="w-4 h-4 rounded object-cover" />
                        ) : (
                          <Shirt className="w-3 h-3 text-primary" />
                        )}
                        <span className="text-[10px] font-medium text-primary">{g.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Metadata + actions */}
              <div className="border-t border-border/50 p-4 space-y-3">
                <div className="flex flex-wrap gap-1.5">
                  {selectedImage.imageSize && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground bg-secondary/40 px-2 py-1 rounded-md">
                      <Ruler className="w-3 h-3" />
                      {selectedImage.imageSize}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground bg-secondary/40 px-2 py-1 rounded-md">
                    <Calendar className="w-3 h-3" />
                    {formatDate(selectedImage.createdAt)}
                  </span>
                  {selectedImage.modelName && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground bg-secondary/40 px-2 py-1 rounded-md">
                      <Cpu className="w-3 h-3" />
                      {selectedImage.modelName}
                    </span>
                  )}
                  {selectedImage.durationMs != null && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground bg-secondary/40 px-2 py-1 rounded-md">
                      <Clock className="w-3 h-3" />
                      {selectedImage.durationMs < 1000
                        ? `${selectedImage.durationMs}ms`
                        : `${(selectedImage.durationMs / 1000).toFixed(1)}s`}
                    </span>
                  )}
                  {selectedImage.seed != null && (
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(String(selectedImage.seed));
                        setSeedCopied(true);
                        setTimeout(() => setSeedCopied(false), 2000);
                      }}
                      className="inline-flex items-center gap-1 text-[10px] text-muted-foreground bg-secondary/40 px-2 py-1 rounded-md tabular-nums hover:bg-secondary/70 hover:text-foreground transition-colors"
                      title="Kopiraj seed"
                    >
                      {seedCopied ? <Check className="w-3 h-3 text-emerald-500" /> : <Dices className="w-3 h-3" />}
                      {selectedImage.seed}
                    </button>
                  )}
                </div>

                {(onUseAsReference || onUpscale) && (
                  <div className="flex gap-1.5">
                    {onUseAsReference && (
                      <button
                        onClick={() => { onUseAsReference(selectedImage.imageUrl); close(); }}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 text-[11px] font-medium rounded-lg border border-primary/30 text-primary hover:bg-primary/10 transition-colors"
                      >
                        <ImagePlus className="w-3.5 h-3.5" />
                        Referenca
                      </button>
                    )}
                    {onUpscale && (
                      <button
                        onClick={() => { onUpscale(selectedImage.imageUrl); close(); }}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 text-[11px] font-medium rounded-lg border border-emerald-500/30 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/10 transition-colors"
                      >
                        <ArrowUpCircle className="w-3.5 h-3.5" />
                        Uvećaj
                      </button>
                    )}
                  </div>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={handleDownload}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 text-[11px] font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Preuzmi
                  </button>
                  <button
                    onClick={() => window.open(selectedImage.imageUrl, "_blank")}
                    className="flex items-center justify-center gap-1.5 px-3 py-2 text-[11px] font-medium rounded-lg border border-border text-muted-foreground hover:text-foreground hover:border-primary/30 transition-colors"
                    title="Otvori u punoj veličini"
                  >
                    <Maximize2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
